import React from 'react';
import { Box, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import type { Readiness, Simulation, Summary } from '../../services/household-retirement-service';
import { monthYear, money } from './format';

/**
 * Readiness at retirement: the projected portfolio against what the
 * withdrawal rate requires, the gap, and the three levers that close it —
 * alongside the Monte Carlo success rate and sustainable spending.
 */
export const ReadinessCard: React.FC<{
  readiness: Readiness;
  summary: Summary;
  simulation?: Simulation | null;
  currency?: string;
}> = ({ readiness: r, summary, simulation, currency = 'CAD' }) => {
  const m = (v: number) => money(v, currency);
  const success = simulation ? simulation.success_rate : null;
  const successColor = success == null ? 'default' : success >= 0.9 ? 'success' : success >= 0.75 ? 'warning' : 'error';
  return (
    <Card data-testid="readiness-card">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="overline" color="text.secondary">Readiness at retirement ({monthYear(r.household_retirement)})</Typography>
          <Chip size="small" color={r.on_track ? 'success' : 'warning'} label={r.on_track ? 'On track' : 'Gap'} variant="outlined" />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 1 }} divider={<Divider orientation="vertical" flexItem />}>
          <Box>
            <Typography variant="caption" color="text.secondary">Projected investable (today's $)</Typography>
            <Typography variant="h5" className="fw-num">{m(r.projected)}</Typography>
            <Typography variant="caption" color="text.secondary">
              Required at {r.withdrawal_rate_pct}%: {m(r.required)} · first-year draw {m(r.first_year_draw)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{r.gap > 0 ? 'Gap' : 'Surplus'}</Typography>
            <Typography variant="h5" className="fw-num" color={r.gap > 0 ? 'warning.main' : 'success.main'}>{m(Math.abs(r.gap))}</Typography>
            <Typography variant="caption" color="text.secondary">Rule-based sustainable spending {m(r.rule_sustainable_spending)}/yr</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Monte Carlo success</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5" className="fw-num">{success == null ? '…' : `${(success * 100).toFixed(1)}%`}</Typography>
              {success != null && <Chip size="small" color={successColor as any} label={`${simulation?.withdrawal_strategy} withdrawals`} variant="outlined" />}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {simulation
                ? `Sustainable at ${simulation.success_target_pct}%: ${m(simulation.sustainable_spending)}/yr core spending`
                : 'Simulating…'}
            </Typography>
          </Box>
        </Stack>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="body2">{r.explanation}</Typography>
        {r.backstop && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }} data-testid="backstop">{r.backstop}</Typography>}
        {!r.on_track && (
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }} aria-label="Levers">
            {r.levers.extra_monthly != null && <li><Typography variant="body2">Invest {m(r.levers.extra_monthly)} more per month until retirement</Typography></li>}
            {r.levers.retire_later_years != null && <li><Typography variant="body2">Retire {r.levers.retire_later_years} years later</Typography></li>}
            {r.levers.spend_less_pct != null && <li><Typography variant="body2">Spend {r.levers.spend_less_pct}% less in retirement</Typography></li>}
          </Box>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 1.5 }}>
          {summary.at_65.map((a) => (
            <Box key={a.person_id}>
              <Typography variant="caption" color="text.secondary">{a.name} at 65 ({monthYear(a.date)})</Typography>
              <Typography variant="body2" className="fw-num">
                Net worth {m(a.net_worth)} · investable {m(a.investable)} ({m(a.investable_real)} today)
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
